import { useContext } from 'react';
import i18next, { t } from 'i18next';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { ChannelOutput } from 'js/model/rainbow/content/ChannelOutput';
import { CmsBrowsePage, CmsFilters } from 'js/model/cms/cms-browse-page';
import { CmsVenuePage } from 'js/model/cms/cms-venue-page';
import { Context } from 'js/components/LocaleWrapper';
import { formatPrice } from 'js/helpers/price/priceUtilities';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import {
  AddLocation,
  browseRemovalParameterType,
  changeBrowseDataContext,
  ChangeBrowseDataFunction,
} from '../BrowseData';
import { createNonLinearSteps } from './nonLinearSteps';
import { RadioOption } from '../FilterModal/RadioGroupFilter';
import {
  PRICE_RANGE_FILTER_EXPONENT,
  getPriceRangeLowerBound,
  getPriceRangeUpperBound,
  isPriceRangeFilterActive,
  isPriceRangeFilterAvailable,
  findClosest,
} from './price-range-filter';
import { getSelectedSalons } from './salons-filter';
import { buildSortOptions, isSortFilterActive } from './sort-filter';
import {
  getSelectedTreatmentTypes,
  isTreatmentTypesFilterActive,
} from './treatment-types-filter';
import { isRatingFilterActive } from '../FilterModal/RatingFilter';

export type Filter = {
  id:
    | 'sortBy'
    | 'price'
    | 'amenities'
    | 'brands'
    | 'salons'
    | 'treatmentTypes'
    | 'rating';
  title: string;
  chipLabel: string;
  isActive: boolean;
  count?: number;
};

const PRICE_RANGE_FILTER_STEPS = 20;
const EN_DASH = '\u2013';

const getSelectedOrDefaultLabel = (
  selectedOptions: string[],
  defaultLabel: string
): string => {
  if (selectedOptions.length === 1) {
    return selectedOptions[0];
  }

  return defaultLabel;
};

const buildParametersToRemove = (
  criteria: VenueListingCriteria
): browseRemovalParameterType[] => {
  const parametersToRemove: browseRemovalParameterType[] = [
    browseRemovalParameterType.MAXDURATION,
    browseRemovalParameterType.ACCOLADE,
    browseRemovalParameterType.BOOKINGTYPE,
  ];

  if (!criteria.price) {
    parametersToRemove.push(browseRemovalParameterType.PRICERANGEFROM);
    parametersToRemove.push(browseRemovalParameterType.PRICERANGETO);
  }

  if (!criteria.amenityIds) {
    parametersToRemove.push(browseRemovalParameterType.AMENITIES);
  }

  if (!criteria.productBrandIds) {
    parametersToRemove.push(browseRemovalParameterType.BRANDS);
  }

  if (!criteria.minReviewRating) {
    parametersToRemove.push(browseRemovalParameterType.MIN_REVIEW_RATING);
  }

  return parametersToRemove;
};

const clearAllFilters = (changeBrowseData: ChangeBrowseDataFunction) => {
  const criteria: VenueListingCriteria = {
    sort: 'recommended',
    newVenues: false,
    yieldDiscounted: false,
  };

  changeBrowseData(
    criteria,
    buildParametersToRemove(criteria),
    AddLocation.CurrentPageWithRadius
  );
};

const applyFilters = (
  changeBrowseData: ChangeBrowseDataFunction,
  criteria: VenueListingCriteria
) => {
  changeBrowseData(
    criteria,
    buildParametersToRemove(criteria),
    AddLocation.CurrentPageWithRadius
  );
};

const getSortFilterLabel = (
  { sort }: VenueListingSpecificationOutput,
  sortOptions: RadioOption<SearchOptions['sortOptions'][number]>[],
  cmsFilters: CmsFilters
) => {
  const sortOption = sortOptions.find(
    (sortOption) => sortOption.value === sort
  );
  const label = sortOption?.label ?? cmsFilters['sort-options'].recommended;

  return sortOption?.value === 'recommended'
    ? cmsFilters['category-label-sort']
    : label;
};

const getPriceRangeFilterLabel = (
  searchOptions: SearchOptions,
  specification: VenueListingSpecificationOutput,
  cmsFilters: CmsFilters,
  channel: ChannelOutput
) => {
  if (isPriceRangeFilterActive(searchOptions, specification)) {
    const availableSteps = createNonLinearSteps(
      searchOptions.minServicePrice!,
      searchOptions.maxServicePrice!,
      PRICE_RANGE_FILTER_STEPS,
      PRICE_RANGE_FILTER_EXPONENT
    );
    const lowerBound = findClosest(
      availableSteps,
      getPriceRangeLowerBound(searchOptions, specification)
    );
    const upperBound = findClosest(
      availableSteps,
      getPriceRangeUpperBound(searchOptions, specification)
    );
    const lowerBoundPrice = formatPrice(lowerBound, channel);
    const upperBoundPrice = formatPrice(upperBound, channel);

    return `${lowerBoundPrice} ${EN_DASH} ${upperBoundPrice}`;
  }

  return cmsFilters['any-price'];
};

const getAmenitiesFilterLabel = (
  specification: VenueListingSpecificationOutput,
  title: string
) => {
  const amenities =
    specification.amenities?.map((amenity) => amenity.name) ?? [];

  if (amenities.length === 1) {
    return amenities[0];
  }

  return title;
};

const getBrandsFilterLabel = (
  specification: VenueListingSpecificationOutput,
  title: string
) => {
  const brands = specification.productBrands?.map((brand) => brand.name) ?? [];

  return getSelectedOrDefaultLabel(brands, title);
};

const getSalonsFilterLabel = (selectedSalons: string[], title: string) =>
  getSelectedOrDefaultLabel(selectedSalons, title);

const getTreatmentTypesFilterLabel = (
  selectedTreatmentTypes: string[],
  title: string
) => getSelectedOrDefaultLabel(selectedTreatmentTypes, title);

export const useFilters = (
  aggregations: Aggregations,
  searchOptions: SearchOptions,
  specification: VenueListingSpecificationOutput
): {
  filters: Filter[];
  hasActiveFilters: boolean;
  applyFilters: (
    criteria: VenueListingCriteria,
    removeParameters?: browseRemovalParameterType[],
    addLocation?: AddLocation
  ) => void;
  clearFilters: () => void;
} => {
  const { channel, pageData } = useContext(Context);
  const changeBrowseData = useContext(changeBrowseDataContext);
  const cmsFilters = (pageData.cms as CmsBrowsePage).page.browse.filters;
  const availbleAmenities = aggregations.amenities || [];
  const availableBrands = aggregations.brands || [];
  const selectedSalons = getSelectedSalons(specification, cmsFilters);
  const salonsFilterTitle = i18next.t('browsePage.filter.salons.title');
  const salonsFilter: Filter = {
    id: 'salons',
    title: salonsFilterTitle,
    chipLabel: getSalonsFilterLabel(selectedSalons, salonsFilterTitle),
    isActive: selectedSalons.length > 0,
  };
  const selectedTreatmentTypes = getSelectedTreatmentTypes(
    specification,
    cmsFilters
  );
  const treatmentTypesFilterTitle: string = i18next.t(
    'browsePage.filter.treatmentTypes'
  );
  const treatmentTypesFilter: Filter = {
    id: 'treatmentTypes',
    title: treatmentTypesFilterTitle,
    chipLabel: getTreatmentTypesFilterLabel(
      selectedTreatmentTypes,
      treatmentTypesFilterTitle
    ),
    isActive: isTreatmentTypesFilterActive(selectedTreatmentTypes),
  };
  const sortOptions = buildSortOptions(searchOptions, cmsFilters);
  const filters: [Filter & { id: 'sortBy' }, ...Filter[]] = [
    {
      id: 'sortBy',
      title: cmsFilters['category-label-sort'],
      chipLabel: getSortFilterLabel(specification, sortOptions, cmsFilters),
      isActive: isSortFilterActive(specification),
    },
  ];

  if (isPriceRangeFilterAvailable(searchOptions)) {
    filters.push({
      id: 'price',
      title: cmsFilters['category-label-price'],
      chipLabel: getPriceRangeFilterLabel(
        searchOptions,
        specification,
        cmsFilters,
        channel
      ),
      isActive: isPriceRangeFilterActive(searchOptions, specification),
    });
  }

  if (availbleAmenities.length > 0) {
    const title = (pageData.cms as CmsVenuePage).page.venue.amenities.amenities;
    const selectedAmenities = specification.amenities ?? [];
    const amenitiesCount = selectedAmenities.length;
    const amenitiesFilter: Filter = {
      id: 'amenities',
      title,
      chipLabel: getAmenitiesFilterLabel(specification, title),
      isActive: amenitiesCount > 0,
    };

    if (amenitiesCount > 1) {
      amenitiesFilter.count = amenitiesCount;
    }

    filters.push(amenitiesFilter);
  }

  if (availableBrands.length > 0) {
    const title = cmsFilters.brands.brands;
    const selectedBrands = specification.productBrands ?? [];
    const brandsCount = selectedBrands.length;
    const brandsFilter: Filter = {
      id: 'brands',
      title,
      chipLabel: getBrandsFilterLabel(specification, title),
      isActive: brandsCount > 0,
    };

    if (brandsCount > 1) {
      brandsFilter.count = brandsCount;
    }

    filters.push(brandsFilter);
  }

  if (selectedSalons.length > 1) {
    salonsFilter.count = selectedSalons.length;
  }

  filters.push(salonsFilter);

  if (selectedTreatmentTypes.length > 1) {
    treatmentTypesFilter.count = selectedTreatmentTypes.length;
  }

  filters.push(treatmentTypesFilter);

  filters.push({
    id: 'rating',
    chipLabel: specification.minReviewRating
      ? t('browsePage.filter.rating.value', {
          count: specification.minReviewRating,
        })
      : t('browsePage.filter.rating.title'),
    isActive: isRatingFilterActive(specification),
    title: t('browsePage.filter.rating.title'),
  });

  return {
    filters,
    hasActiveFilters: filters.some((filter) => filter.isActive),
    applyFilters: (criteria) => {
      applyFilters(changeBrowseData, criteria);
    },
    clearFilters: () => {
      clearAllFilters(changeBrowseData);
    },
  };
};
